/*------------------------------------------------------------------
External
------------------------------------------------------------------*/

import axios from 'axios';


/*------------------------------------------------------------------
Routes
------------------------------------------------------------------*/

const RCM_API = 'api/rcm';
const AGENT_API = 'api/agent';
const POST_BOOKING = 'api/rcm_postbooking';
const SEND_EXTERNAL_EMAIL = 'api/send_external_email';
const CURRENCY = '//openexchangerates.org/api/latest.json?app_id=724819077ed84b55822bebd0f678a130';


/*------------------------------------------------------------------
Data extraction helper
------------------------------------------------------------------*/

export const extractData = ({ data }) => data;


/*------------------------------------------------------------------
API items
------------------------------------------------------------------*/

/**
* 
* Fetch a list of all of the currencies from RCM
*
* @param {object} data passthrough data needed to give to RCM
* @returns {promise}  promise object with the booking data
*/
export const fetch_currencies = (data = {}) =>
    axios
        .post(CURRENCY, {})
        .then(extractData);


/**
* 
* Search for a booking based on a last name and booking number
* firstname is not used but included for consistency
* 
* @param {object} data passthrough data needed to give to RCM
* @returns {promise}  promise object with the booking data
*/
export const search_for_booking = async (data = {}) =>
    await axios
        .post(RCM_API, {
            method: 'bookinginfo',
            reservationno: data.reservation_number,
            lastname: data.last_name,
            firstname: data.first_name,
        })
        .then(extractData);


/**
* 
* Search for a booking based on a reservation reference
*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const fetch_booking = async (data = {}) =>
    await axios
        .post(RCM_API, {
            method: 'bookinginfo',
            reservationref: data.reservation_reference,
        })
        .then(extractData);


/**
* 
* Start off the booking process by fetching all of the 
* needed location data from RCM
*
* @returns  {promise} promise object with the step 1 data
*/
export const get_step_one = async () =>
    await axios
        .post(RCM_API, {
            method: 'step1'
        })
        .then(extractData);


/**
* 
* Fetch the available cars for a given location/time combination
*
* @returns  {promise} promise object with the step 2 data
*/
export const get_step_two = async (data = {}) =>
    await axios
        .post(RCM_API, {
            method: 'step2',
            vehiclecategorytypeid: data.vehicle_category_type_id || 0,
            ageid: data.age_id,
            pickuplocationid: data.pickup_location_id,
            pickupdate: data.pickup_date,
            pickuptime: data.pickup_time,
            dropofflocationid: data.dropoff_location_id || data.pickup_location_id,
            dropoffdate: data.dropoff_date,
            dropofftime: data.dropoff_time,
            campaigncode: data.campaign_code,
            agentcode: data.agent_code
        })
        .then(extractData);


/**
* 
* Fetch the associated information attached to a specifc vehicle
*
* @returns  {promise} promise object with the step 3 data
*/
export const get_step_three = async (data = {}) =>
    await axios
        .post(RCM_API, {
            method: 'step3',
            vehiclecategorytypeid: data.vehicle_category_type_id || 0,
            ageid: data.age_id,
            pickuplocationid: data.pickup_location_id,
            pickupdate: data.pickup_date,
            pickuptime: data.pickup_time,
            dropofflocationid: data.dropoff_location_id || data.pickup_location_id,
            dropoffdate: data.dropoff_date,
            dropofftime: data.dropoff_time,
            vehiclecategoryid: data.vehicle_category_id,
            campaigncode: data.campaign_code,
            relocationspecialid: data.relocation_special_id,
            agentcode: data.agent_code
        })
        .then(extractData);


/**
* 
* Make a booking or get a quote
*
* @returns  {promise} promise object with the booking data
*/
export const make_booking = async (data = {}) =>
    await axios
        .post(RCM_API, {
            method: 'booking',
            vehiclecategorytypeid: data.vehicle_category_type_id || 0,
            pickuplocationid: data.pickup_location_id,
            pickupdate: data.pickup_date,
            pickuptime: data.pickup_time,
            dropofflocationid: data.dropoff_location_id || data.pickup_location_id,
            dropoffdate: data.dropoff_date,
            dropofftime: data.dropoff_time,
            ageid: data.age_id || window.defaults.age_id,
            vehiclecategoryid: data.vehicle_category_id,
            bookingtype: data.booking_type,
            insuranceid: data.insurance_id,
            extrakmsid: data.extra_kms_id,
            transmission: data.transmission,
            emailoption: data.email_option,
            referralid: data.referral_id,
            campaigncode: data.campaign_code,
            agentcode: data.agent_code,
            agentname: data.agent_name,
            agentemail: data.agent_email,
            agentrefno: data.agent_ref_no,
            foundusid: data.found_us_id,
            remark: data.remark,
            numbertravelling: data.number_travelling,
            flightin: data.flight_in,
            flightout: data.flight_out,
            arrivalpoint: data.arrival_point,
            departurepoint: data.departure_point,
            areaofuseid: data.area_of_use_id,
            newsletter: data.newsletter,
            refno: data.ref_no,
            relocationspecialid: data.relocation_special_id,
            customer: {
                firstname: data.customer.first_name,
                lastname: data.customer.last_name,
                email: data.customer.email
            },
            extradriver: data.extra_driver || [],
            optionalfees: data.optional_fees || []
        })
        .then(extractData);


/**
*
* Convert an existing quote in to a booking
*
* @returns  {promise} promise object with the booking data
*/
export const convert_quote = async (data = {}) =>
    await axios
        .post(POST_BOOKING, {
            method: 'convertquote',
            reservationref: data.resservation_reference,
            insuranceid: data.insurance_id,
            extrakmsid: data.extra_kms_id,
            emailoption: data.email_option,
            referralid: data.referral_id,
            remark: data.remark,
            numbertravelling: data.number_travelling,
            flightin: data.flight_in,
            flightout: data.flight_out,
            arrivalpoint: data.arrival_point,
            departurepoint: data.departure_point,
            areaofuseid: data.area_of_use_id,
            newsletter: data.newsletter,
            agentcode: data.agent_code,
            agentname: data.agent_name,
            agentemail: data.agent_email,
            agentrefno: data.agent_reference_number,
            customer: {
                firstname: data.customer.first_name,
                lastname: data.customer.last_name,
                email: data.customer.email
            },
            optionalfees: data.optional_fees || [],

        })
        .then(extractData);


/**
* 
* Search for a booking based on a reservation reference for
* post booking manipulation
*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const fetch_booking_post = async (data = {}) =>
    await axios
        .post(POST_BOOKING, {
            method: 'bookinginfo',
            reservationref: data.reservation_reference,
        })
        .then(extractData);


/**
* 
* Fetch the workflow checklist associated to a booking
*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const fetch_workflow_checklist = async (data = {}) =>
    await axios
        .post(POST_BOOKING, {
            method: 'workflowchecklist',
            reservationref: data.reservation_reference,
            workflowcode: `checkin`
        })
        .then(extractData);


/**
* 
* Search for a booking based on a reservation reference
*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const find_booking_post = async (data = {}) =>
    await axios
        .post(POST_BOOKING, {
            method: 'findbooking',
            reservationno: data.reservation_number,
            lastname: data.last_name,
            firstname: data.first_name,
        })
        .then(extractData);


/**
* 
* Send an email to the customer or agent 
*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const send_email = async (data = {}) =>
    await axios
        .post(POST_BOOKING, {
            method: 'sendemail',
            reservationref: data.reservation_number,
            emailtypename: data.email_type_name,
            workflowcode: data.workflow_code,
        })
        .then(extractData);


/**
 * 
 * Fetch a list of all of the cards saved to a specific booking
 * 
 * @param {string} reservation_reference 
 * @param {number} supplier_id
 * @returns {promise} promise object with the vault list data
 */
export const fetch_vault_list = async ({ reservation_reference, supplier_id }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'vaultlist',
            reservationref: reservation_reference,
            supplierid: supplier_id
        })
        .then(extractData);


/**
 * 
 * Fetch a list of all of the saved signatures saved to a specific booking
 * 
 * @param {string} reservation_reference 
 * @returns {promise} promise object with the booking data
 */
export const fetch_signature_list = async ({ reservation_reference }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'signaturelist',
            reservationref: reservation_reference,
            workflowcode: 'checkin'
        })
        .then(extractData);


/**
 * 
 * Save a signature against a user for a specific booking
 * 
 * @param string} reservation_reference 
 * @param {number} signature_template_id 
 * @param {array} signature_json 
 * @param {number} extra_driver_id 
 * @returns 
 */
export const save_signature = async ({ reservation_reference, signature_template_id, signature_json, extra_driver_id }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'savesignature',
            reservationref: reservation_reference,
            signaturetemplateid: signature_template_id,
            signaturejson: signature_json,
            extradriverid: extra_driver_id,
            workflowcode: 'checkin'
        })
        .then(extractData);


/**
 * 
 * Remove a signature against a user for a specific booking and overide it with a blank 1x1 png 
 * 
 * @param string} reservation_reference 
 * @param {number} signature_template_id 
 * @param {array} signature_json 
 * @param {number} extra_driver_id 
 * @returns 
 */
export const remove_signature = async ({ reservation_reference, signature_template_id, extra_driver_id }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'savesignature',
            reservationref: reservation_reference,
            signaturetemplateid: signature_template_id,
            signaturepng: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+ip1sAAAAASUVORK5CYII=',
            extradriverid: extra_driver_id,
            workflowcode: 'checkin'
        })
        .then(extractData);


/**
 * 
 * Remove an extra driver from a booking
 * 
 * @param {object} data passthrough data needed to give to RCM
 * @returns {promise} promise object with the booking data
 */
export const remove_extra_driver = async ({ reservation_reference, customer_id, first_name, last_name, email }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'extradriver',
            reservationref: reservation_reference,
            customerid: customer_id,
            customer: {
                firstname: first_name,
                lastname: last_name,
                email: email,
            }
        })
        .then(extractData);


/**
 * 
 * Add an extra driver to a booking
 * 
 * @param {object} data passthrough data needed to give to RCM
 * @returns {promise} promise object with the booking data
 */
export const add_extra_driver = async ({ reservation_reference, customer_id, first_name, last_name, email, date_of_birth, phone, license_number, license_expiry, license_issue_country, address, state, city, country, postcode }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'extradriver',
            reservationref: reservation_reference,
            customerid: customer_id,
            customer: {
                firstname: first_name,
                lastname: last_name,
                email: email,
                dateofbirth: date_of_birth,
                phone: phone,
                licenseno: license_number,
                licenseexpires: license_expiry,
                licenseissued: license_issue_country,
                address: address,
                state: state,
                city: city,
                countryid: country,
                postcode: postcode,

            }
        })
        .then(extractData);


/**
 * 
 * Edit a booking through the portal
 * 
 * @param {object} data passthrough data needed to give to RCM
 * @returns {promise} promise object with the booking data
 */
export const edit_booking_portal = async ({ reservation_reference, customer_id, optional_fees, insurance_id, number_of_people_travelling, booking_type, first_name, last_name, email, date_of_birth, phone, mobile, license_number, license_expiry, license_issue_country, address, state, city, country, postcode, remarks }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'editbooking',
            reservationref: reservation_reference,
            customerid: customer_id,
            bookingtype: booking_type,
            extrakmsid: 0,
            insuranceid: insurance_id,
            transmission: 1,
            numbertravelling: number_of_people_travelling,
            emailoption: 2,
            remark: remarks,
            customer: {
                firstname: first_name,
                lastname: last_name,
                email: email,
                dateofbirth: date_of_birth,
                phone: phone,
                mobile: mobile,
                licenseno: license_number,
                licenseexpires: license_expiry,
                licenseissued: license_issue_country,
                address: address,
                state: state,
                city: city,
                countryid: country,
                postcode: postcode,
            },
            optionalfees: optional_fees
        })
        .then(extractData);

/**
* 
* Update a booking through the portal 
* In both cases, reservationref, bookingtype, pickupdatetime, pickuplocationid, dropoffdatetime, dropofflocationid, vehiclecategoryid and driverageid are all required.
* 
* @param {object} data passthrough data needed to give to RCM
* @returns {promise} promise object with the booking data
*/
export const update_booking_portal = async ({ reservation_reference, booking_type, pick_up_date_time, pick_up_location_id, drop_off_date_time, drop_off_location_id, vehicle_category_id, driver_age_id, customer_id, number_of_people_travelling, insurance_id, remarks, first_name, last_name, email, date_of_birth, phone, mobile, license_number, license_expiry, license_issue_country, address, state, city, country, postcode, optional_fees }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'updatebooking',
            reservationref: reservation_reference,
            bookingtype: booking_type,
            pickupdatetime: pick_up_date_time,
            pickuplocationid: pick_up_location_id,
            dropoffdatetime: drop_off_date_time,
            dropofflocationid: drop_off_location_id,
            vehiclecategoryid: vehicle_category_id,
            driverageid: driver_age_id,
            customerid: customer_id,
            extrakmsid: 0,
            insuranceid: insurance_id,
            transmission: 1,
            numbertravelling: number_of_people_travelling,
            emailoption: 2,
            remark: remarks,
            customer: {
                firstname: first_name,
                lastname: last_name,
                email: email,
                dateofbirth: date_of_birth,
                phone: phone,
                mobile: mobile,
                licenseno: license_number,
                licenseexpires: license_expiry,
                licenseissued: license_issue_country,
                address: address,
                state: state,
                city: city,
                countryid: country,
                postcode: postcode,
            },
            optionalfees: optional_fees

        })
        .then(extractData);


/**
* 

*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const sign_upload = async ({ timestamp }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'signupload',
            params: {
                callback: "https://widget.cloudinary.com/cloudinary_cors.html",
                upload_preset: "hsfepzh0",
                timestamp: timestamp
            }
        })
        .then(extractData);


/**
* 

*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const store_upload = async ({ reservation_reference, url, customerid }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'storeupload',
            reservationref: reservation_reference,
            url: url,
            customerid: customerid,
            documentlinksetupid: 1,
            source: 'API 3.2',
            doctype: 'Drivers License',
            wordescriptionkflowcode: 'Uploaded image of drivers license from user',
            notes: 'Uploaded via web platform using DropZone',
        })
        .then(extractData);

/**
* 

*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const delete_upload = async ({ reservation_reference, document_id }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'editupload',
            reservationref: reservation_reference,
            documentlinkid: document_id
        })
        .then(extractData);


/**
* 
* Fetch a list of all of the saved signatures saved to a specific booking
* 
* @param {string} reservation_reference 
* @returns {promise} promise object with the booking data
*/
export const fetch_document_list = async ({ reservation_reference }) =>
    await axios
        .post(POST_BOOKING, {
            method: 'documentlist',
            reservationref: reservation_reference,
            workflowcode: 'checkin'
        })
        .then(extractData);



/**
* 
* Fetch a list of all of the saved signatures saved to a specific booking
* 
* @param {string} reservation_reference 
* @returns {promise} promise object with the booking data
*/
export const send_external_email = async ({ template_id, first_name, email, store_email }) =>
    await axios
        .post(SEND_EXTERNAL_EMAIL, {
            template_id,
            first_name,
            email,
            store_email
        })
        .then(extractData);


/**
* 
* Fetch the available cars for a given location/time combination
*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const probe_for_agent = async (data = {}) =>
    await axios
        .post(AGENT_API, {
            method: 'step2',
            agentcode: data.agent_code,
            selected_country: data.selected_country,
        })
        .then(extractData);


/**
* 
* Fetch the available cars for a given location/time combination
*
* @returns  {promise} promise object with the step 2 data
*/
export const get_step_two_agent = async (data = {}) =>
    await axios
        .post(AGENT_API, {
            method: 'step2',
            vehiclecategorytypeid: data.vehicle_category_type_id || 0,
            ageid: data.age_id,
            pickuplocationid: data.pickup_location_id,
            pickupdate: data.pickup_date,
            pickuptime: data.pickup_time,
            dropofflocationid: data.dropoff_location_id || data.pickup_location_id,
            dropoffdate: data.dropoff_date,
            dropofftime: data.dropoff_time,
            campaigncode: data.campaign_code,
            agentcode: data.agent_code,
            selected_country: data.selected_country,
        })
        .then(extractData);



/**
* 
* Fetch the associated information attached to a specifc vehicle
*
* @returns  {promise} promise object with the step 3 data
*/
export const get_step_three_agent = async (data = {}) =>
    await axios
        .post(AGENT_API, {
            method: 'step3',
            vehiclecategorytypeid: data.vehicle_category_type_id || 0,
            ageid: data.age_id,
            pickuplocationid: data.pickup_location_id,
            pickupdate: data.pickup_date,
            pickuptime: data.pickup_time,
            dropofflocationid: data.dropoff_location_id || data.pickup_location_id,
            dropoffdate: data.dropoff_date,
            dropofftime: data.dropoff_time,
            vehiclecategoryid: data.vehicle_category_id,
            campaigncode: data.campaign_code,
            relocationspecialid: data.relocation_special_id,
            agentcode: data.agent_code,
            selected_country: data.selected_country,
        })
        .then(extractData);



/**
* 
* Make a booking or get a quote for an agent
*
* @returns  {promise} promise object with the booking data
*/
export const make_booking_agent = async (data = {}) =>
    await axios
        .post(AGENT_API, {
            method: 'booking',
            vehiclecategorytypeid: data.vehicle_category_type_id || 0,
            pickuplocationid: data.pickup_location_id,
            pickupdate: data.pickup_date,
            pickuptime: data.pickup_time,
            dropofflocationid: data.dropoff_location_id || data.pickup_location_id,
            dropoffdate: data.dropoff_date,
            dropofftime: data.dropoff_time,
            ageid: data.age_id || window.defaults.age_id,
            vehiclecategoryid: data.vehicle_category_id,
            bookingtype: data.booking_type,
            insuranceid: data.insurance_id,
            extrakmsid: data.extra_kms_id,
            transmission: data.transmission,
            emailoption: data.email_option,
            referralid: data.referral_id,
            campaigncode: data.campaign_code,
            agentcode: data.agent_code,
            agentname: data.agent_name,
            agentemail: data.agent_email,
            agentrefno: data.agent_ref_no,
            foundusid: data.found_us_id,
            remark: data.remark,
            numbertravelling: data.number_travelling,
            flightin: data.flight_in,
            flightout: data.flight_out,
            arrivalpoint: data.arrival_point,
            departurepoint: data.departure_point,
            areaofuseid: data.area_of_use_id,
            newsletter: data.newsletter,
            refno: data.ref_no,
            relocationspecialid: data.relocation_special_id,
            customer: {
                firstname: data.customer.first_name,
                lastname: data.customer.last_name,
                email: data.customer.email || data.agent_email
            },
            extradriver: data.extra_driver || [],
            optionalfees: data.optional_fees || [],
            selected_country: data.selected_country
        })
        .then(extractData);



/**
* 
* Search for a booking based on a reservation reference for
* post booking manipulation
*
* @param {object} data passthrough data needed to give to RCM
* @returns  {promise} promise object with the booking data
*/
export const fetch_booking_post_agent = async (data = {}) =>
    await axios
        .post(AGENT_API, {
            method: 'bookinginfo',
            reservationref: data.reservation_reference,
            selected_country: data.selected_country,
        })
        .then(extractData);